import { useCallback } from 'react';
import { EcardLookupPayload } from '../types/EcardLookupPayload';
import { generateShareableLinkUrl } from '../utils/generateShareableLinkUrl';
import { EcardDesignInputFields } from '@shared/components/EmailsAndEcards/components/Design/Design.controller';
import { ECardDraft } from '@apps/saveTheDate/SaveTheDate.types';
import { getEcardPayload } from '@apps/ecard/Ecard.utils';
import { EventDesignFragment, EcardType, ElectronicCardInput } from '@graphql/generated';

type ShareableLinkConfig = Readonly<{
  eventId: string;
  eventPassword: string | null;
  saveEcardMutation: (options?: { variables: { payload: ElectronicCardInput } }) => Promise<{ data?: { saveEcard?: { __typename?: string; id: string } | null } | null }>;
  onSaveEcardDesign?: (eventDesign: EventDesignFragment, ecardId: string, ecardInput: EcardDesignInputFields) => void;
  onSuccess?: (ecardId: string) => void;
  onError?: (error: Error, context?: EcardType) => void;
  // Additional fields for SaveTheDate
  ecardType?: EcardType;
  location?: string;
  date?: string;
  // For Ecard controller
  eventDesign?: EventDesignFragment | null;
  ecardInput?: EcardDesignInputFields;
  // For SaveTheDate
  eCardDraft?: ECardDraft;
}>;

/**
 * Hook that provides shareable link generation functionality
 * @param config - Configuration object with mutation, callbacks, and optional parameters
 * @returns Object containing generateShareableLink function
 */
export const useShareableCardLinkGenerator = (config: ShareableLinkConfig) => {
  const generateShareableLink = useCallback(async (): Promise<string | null> => {
    try {
      let ecardId: string | undefined;

      if (config.ecardType === EcardType.savethedatephoto) {
        // SaveTheDate logic
        const payload = {
          ...config.eCardDraft,
          title: config.eCardDraft?.title || '',
          eventId: config.eventId,
          type: EcardType.savethedatephoto,
          recipientIds: [],
        };

        // Call success callback if provided
        if (config.onSuccess && config.eCardDraft?.id) {
          config.onSuccess(config.eCardDraft.id);
        }

        ecardId = config.eCardDraft?.id || (await config.saveEcardMutation({ variables: { payload } })).data?.saveEcard?.id;
      } else {
        // Ecard controller logic
        if (!config.ecardInput) return null;
        const eCardPayload = getEcardPayload(config.ecardInput as EcardDesignInputFields);
        const res = await config.saveEcardMutation({ variables: { payload: eCardPayload } });
        ecardId = res.data?.saveEcard?.id;

        if (ecardId && config.onSaveEcardDesign && config.eventDesign) {
          config.onSaveEcardDesign(config.eventDesign, ecardId, config.ecardInput);
        }
      }

      if (ecardId) {
        const lookupPayload: EcardLookupPayload = {
          ecardId,
          personId: 'anonymous',
          isAnonymous: true,
          eventPassword: config.eventPassword,
          ...(config.ecardType && { ecardType: config.ecardType }),
          ...(config.location && { location: encodeURI(config.location) }),
          ...(config.date && { date: config.date }),
        };

        return generateShareableLinkUrl(lookupPayload);
      }

      return null;
    } catch (error) {
      config.onError?.(error as Error, config.ecardType);
      return null;
    }
  }, [config]);

  return { generateShareableLink };
};
